import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import "./ServiceProviderPage.css";
import { getServiceCategoryBySlug } from "@/data/servicesConfig";
import { appendQuote } from "@/components/services/quotesStore";
import { saveEscrow } from "@/components/services/escrowStore";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const TABS = [
  { id: "offers",       label: "Services" },
  { id: "quote",        label: "Devis" },
  { id: "reviews",      label: "Avis" },
  { id: "history",      label: "Historique" },
  { id: "availability", label: "Dispos" },
];

function formatCfa(n) { return typeof n === "number" ? `${n.toLocaleString("fr-FR")} FCFA` : "—"; }
function readUid() { try { return localStorage.getItem("aya_user_id") || "anon"; } catch (_) { return "anon"; } }

// ─── Quote modal ─────────────────────────────────────────
function QuoteModal({ open, onClose, provider, onSubmitted }) {
  const [need, setNeed] = useState("");
  const [desc, setDesc] = useState("");
  const [sending, setSending] = useState(false);
  if (!open) return null;
  const uid = readUid();

  const send = async () => {
    if (!need.trim()) return;
    setSending(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/v3/services/quote`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userId: uid, providerSlug: provider.slug, need, description: desc }),
      });
      const json = await res.json();
      if (json.ok && json.quote) {
        appendQuote(uid, json.quote);
        onSubmitted?.(json.quote);
      }
    } catch (_) { /* mock fallback */ }
    setSending(false);
    onClose();
  };

  return (
    <div className="service-modal-overlay" onClick={onClose} data-testid="service-quote-modal">
      <div className="service-modal" onClick={(e) => e.stopPropagation()}>
        <h2 className="service-modal__title">Demander un devis</h2>
        <p className="service-modal__hint">À {provider.name}</p>
        <input
          className="service-modal__input"
          placeholder="Type de besoin (ex: Installation tableau)"
          value={need}
          onChange={(e) => setNeed(e.target.value)}
          data-testid="quote-need"
        />
        <textarea
          className="service-modal__textarea"
          placeholder="Détaille ton besoin…"
          value={desc}
          onChange={(e) => setDesc(e.target.value)}
          data-testid="quote-desc"
        />
        <p className="service-modal__hint">MOCK: les photos seront traitées côté serveur en sprint K2.</p>
        <div className="service-modal__row">
          <button type="button" className="service-modal__btn service-modal__btn--secondary" onClick={onClose}>Annuler</button>
          <button type="button" className="service-modal__btn service-modal__btn--primary" onClick={send} disabled={sending || !need.trim()} data-testid="quote-submit">
            {sending ? "Envoi…" : "Envoyer"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Escrow modal (3 steps) ──────────────────────────────
function EscrowModal({ open, onClose, provider }) {
  const [step, setStep] = useState(0); // 0=lock, 1=in_progress, 2=done
  const [escrow, setEscrow] = useState(null);
  const [amount, setAmount] = useState("15000");
  const uid = readUid();

  useEffect(() => { if (open) { setStep(0); setEscrow(null); } }, [open]);
  if (!open) return null;

  const doLock = async () => {
    const amt = parseInt(amount, 10);
    if (!amt) return;
    const res = await fetch(`${BACKEND_URL}/api/v3/services/escrow/lock`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ userId: uid, providerSlug: provider.slug, amount_fcfa: amt, description: `Mission ${provider.role}` }),
    });
    const json = await res.json();
    if (json.ok) { setEscrow(json.escrow); saveEscrow(uid, json.escrow); setStep(1); }
  };
  const doRelease = async () => {
    if (!escrow) return;
    const res = await fetch(`${BACKEND_URL}/api/v3/services/escrow/release`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ escrow_id: escrow.id }),
    });
    const json = await res.json();
    if (json.ok) { setEscrow(json.escrow); saveEscrow(uid, json.escrow); setStep(2); }
  };

  return (
    <div className="service-modal-overlay" onClick={onClose} data-testid="escrow-modal">
      <div className="service-modal" onClick={(e) => e.stopPropagation()}>
        <h2 className="service-modal__title">Paiement sécurisé (escrow)</h2>
        <div className="escrow-steps">
          <div className={`escrow-step${step >= 0 ? " is-active" : ""}`}>1. Verrouiller</div>
          <div className={`escrow-step${step >= 1 ? " is-active" : ""}`}>2. En cours</div>
          <div className={`escrow-step${step >= 2 ? " is-active" : ""}`}>3. Libéré</div>
        </div>
        {step === 0 && (
          <>
            <p className="service-modal__hint">MOCK ESCROW : le montant est bloqué sur ton wallet, libéré uniquement quand tu confirmes la fin de mission.</p>
            <input className="service-modal__input" type="number" placeholder="Montant FCFA" value={amount} onChange={(e) => setAmount(e.target.value)} data-testid="escrow-amount" />
            <div className="service-modal__row">
              <button type="button" className="service-modal__btn service-modal__btn--secondary" onClick={onClose}>Annuler</button>
              <button type="button" className="service-modal__btn service-modal__btn--primary" onClick={doLock} data-testid="escrow-lock-btn">Verrouiller {formatCfa(parseInt(amount, 10))}</button>
            </div>
          </>
        )}
        {step === 1 && escrow && (
          <>
            <p className="service-modal__hint">Escrow <strong style={{ color: "var(--aya-gold-200)" }}>{escrow.id}</strong> verrouillé. Mission en cours avec {provider.name}.</p>
            <p style={{ color: "var(--aya-gold-200)", textAlign: "center", margin: "16px 0", fontFamily: "Fraunces, serif", fontSize: 18 }}>{formatCfa(escrow.amount_fcfa)} bloqués</p>
            <div className="service-modal__row">
              <button type="button" className="service-modal__btn service-modal__btn--secondary" onClick={onClose}>Plus tard</button>
              <button type="button" className="service-modal__btn service-modal__btn--primary" onClick={doRelease} data-testid="escrow-release-btn">Mission terminée → libérer</button>
            </div>
          </>
        )}
        {step === 2 && escrow && (
          <>
            <p className="service-modal__hint" style={{ color: "#34d399" }}>✓ Fonds libérés au prestataire le {new Date(escrow.released_at).toLocaleString("fr-FR")}.</p>
            <button type="button" className="service-modal__btn service-modal__btn--primary" onClick={onClose} style={{ width: "100%", marginTop: 12 }}>Terminer</button>
          </>
        )}
      </div>
    </div>
  );
}

// ─── Referral modal ──────────────────────────────────────
function ReferralModal({ open, onClose, provider }) {
  const uid = readUid();
  if (!open) return null;
  const url = `https://aya.ci/r/${uid}/service/${provider.slug}`;
  const copy = () => { try { navigator.clipboard.writeText(url); } catch (_) { /* non-fatal */ } };
  return (
    <div className="service-modal-overlay" onClick={onClose} data-testid="referral-modal">
      <div className="service-modal" onClick={(e) => e.stopPropagation()}>
        <h2 className="service-modal__title">Recommander ce pro</h2>
        <p className="service-modal__hint">Partage ce lien avec tes proches :</p>
        <input className="service-modal__input" value={url} readOnly data-testid="referral-url" />
        <div className="service-modal__row">
          <button type="button" className="service-modal__btn service-modal__btn--secondary" onClick={onClose}>Fermer</button>
          <button type="button" className="service-modal__btn service-modal__btn--primary" onClick={copy} data-testid="referral-copy">Copier</button>
        </div>
        <div className="service-modal__share-row">
          <a className="service-modal__share-btn" href={`https://wa.me/?text=${encodeURIComponent(url)}`} target="_blank" rel="noreferrer">WhatsApp</a>
          <a className="service-modal__share-btn" href={`sms:?body=${encodeURIComponent(url)}`}>SMS</a>
        </div>
      </div>
    </div>
  );
}

// ─── Page principale ─────────────────────────────────────
export default function ServiceProviderPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [active, setActive] = useState("offers");
  const [provider, setProvider] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quoteOpen, setQuoteOpen] = useState(false);
  const [escrowOpen, setEscrowOpen] = useState(false);
  const [referralOpen, setReferralOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetch(`${BACKEND_URL}/api/v3/services/provider/${encodeURIComponent(slug)}`)
      .then((r) => r.ok ? r.json() : Promise.reject(r.status))
      .then((json) => { if (!cancelled) setProvider(json.provider); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug]);

  const meta = provider ? getServiceCategoryBySlug(provider.category) : null;

  if (loading) return <div className="service-provider-page" data-testid="service-provider-loading"><p style={{ padding: 80, textAlign: "center", color: "#94a3b8" }}>Chargement…</p></div>;
  if (!provider) return <div className="service-provider-page" data-testid="service-provider-error"><p style={{ padding: 80, textAlign: "center", color: "#94a3b8" }}>Prestataire introuvable.</p></div>;

  return (
    <div className="service-provider-page" data-testid="service-provider-page">
      <div className="service-provider-hero">
        <button type="button" className="service-provider-hero__back" onClick={() => navigate(-1)} aria-label="Retour" data-testid="service-provider-back">←</button>
        <span className="service-provider-hero__icon" aria-hidden="true">{meta?.icon || "🛠️"}</span>
      </div>
      <section className="service-provider-identity">
        <p className="service-provider-identity__role">{provider.role}</p>
        <h1 className="service-provider-identity__name" data-testid="service-provider-name">{provider.name}</h1>
        <p style={{ color: "#94a3b8", fontSize: 13, margin: "4px 0 8px" }}>📍 {provider.location}</p>
        <div className="service-provider-identity__pills">
          <span className="service-provider-identity__pill service-provider-identity__pill--rating">★ {provider.rating?.toFixed?.(1)} ({provider.jobs_count})</span>
          {provider.certified && <span className="service-provider-identity__pill service-provider-identity__pill--cert">✓ Certifié AYA</span>}
          <span className="service-provider-identity__pill service-provider-identity__pill--xp">{provider.years_experience} ans XP</span>
        </div>
      </section>

      <div className="service-tabs" role="tablist" data-testid="service-tabs">
        {TABS.map((t) => (
          <button
            key={t.id}
            type="button"
            role="tab"
            aria-selected={active === t.id}
            className={`service-tab${active === t.id ? " is-active" : ""}`}
            onClick={() => setActive(t.id)}
            data-testid={`service-tab-${t.id}`}
          >
            {t.label}
            {active === t.id && <span className="service-tab__underline" />}
          </button>
        ))}
      </div>

      {active === "offers" && (
        <div className="service-tab-content" data-testid="service-offers-tab">
          <h2 className="service-section-title">Spécialités proposées</h2>
          {(provider.specialties || []).map((s, i) => (
            <div key={i} className="service-offer-row">
              <span className="service-offer-row__name">{s}</span>
              <span className="service-offer-row__price">{formatCfa(provider.price_range_cfa?.from)}+</span>
            </div>
          ))}
        </div>
      )}
      {active === "quote" && (
        <div className="service-tab-content" data-testid="service-quote-tab">
          <h2 className="service-section-title">Demander un devis</h2>
          <p style={{ color: "#cbd5e1", fontSize: 14, lineHeight: 1.6 }}>
            Décris ton besoin et reçois une proposition chiffrée sous 2-4h. Le devis est gratuit.
          </p>
          <button type="button" className="service-cta-btn" onClick={() => setQuoteOpen(true)} data-testid="service-quote-cta">Demander un devis →</button>
        </div>
      )}
      {active === "reviews" && (
        <div className="service-tab-content" data-testid="service-reviews-tab">
          <h2 className="service-section-title">Avis ({provider.reviews?.length || 0})</h2>
          {(provider.reviews || []).map((r) => (
            <div key={r.id} style={{ padding: 12, background: "rgba(20,24,28,0.55)", borderRadius: 12, marginBottom: 8 }}>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}>
                <strong style={{ color: "#fff" }}>{r.author}</strong>
                <span style={{ color: "var(--aya-gold-200)" }}>{"★".repeat(r.rating)}</span>
              </div>
              <p style={{ color: "#cbd5e1", fontSize: 13, margin: 0 }}>{r.text}</p>
            </div>
          ))}
        </div>
      )}
      {active === "history" && (
        <div className="service-tab-content" data-testid="service-history-tab">
          <h2 className="service-section-title">Historique avec ce pro</h2>
          <p style={{ color: "#94a3b8", fontSize: 13 }}>Aucune mission passée. Lance ta première demande de devis.</p>
        </div>
      )}
      {active === "availability" && (
        <div className="service-tab-content" data-testid="service-availability-tab">
          <h2 className="service-section-title">Disponibilités</h2>
          {(provider.availability || []).map((a) => (
            <div key={a.date} className="service-offer-row">
              <span className="service-offer-row__name">{new Date(a.date).toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" })}</span>
              <span style={{ color: "var(--aya-gold-200)", fontSize: 13 }}>{a.slots.join(" · ")}</span>
            </div>
          ))}
        </div>
      )}

      <div className="service-floating-cta">
        <button type="button" className="service-floating-cta__btn service-floating-cta__btn--quote" onClick={() => setEscrowOpen(true)} data-testid="service-cta-escrow">Réserver (escrow)</button>
        <button type="button" className="service-floating-cta__btn service-floating-cta__btn--refer" onClick={() => setReferralOpen(true)} data-testid="service-cta-refer">Recommander</button>
      </div>

      <QuoteModal open={quoteOpen} onClose={() => setQuoteOpen(false)} provider={provider} />
      <EscrowModal open={escrowOpen} onClose={() => setEscrowOpen(false)} provider={provider} />
      <ReferralModal open={referralOpen} onClose={() => setReferralOpen(false)} provider={provider} />
    </div>
  );
}
